import { useFlag, useFlagsStatus } from '@unleash/proxy-client-react';
import type { ReactNode } from 'react';
import { unleashEnabled } from '../../lib/feature-flags';

interface FlagGateProps {
  /** Unleash flag name (e.g. 'community-board', 'maintenance-mode'). */
  flag: string;
  /** Rendered when the flag is ON (or OFF when `invert` is true). */
  children: ReactNode;
  /**
   * Rendered when the flag is OFF (or ON when `invert` is true), once the SDK
   * has actually resolved the flag. Defaults to `null` so gated UI is hidden
   * when the flag is off.
   *
   * For route-level gating, pass `<Navigate to="/" replace />`.
   */
  fallback?: ReactNode;
  /**
   * Rendered ONLY during the SDK bootstrap window before flags resolve.
   * Defaults to `null`. For route-level gates, pass a loader so the route shows
   * a spinner instead of briefly redirecting/flashing — without this, a route
   * whose `fallback` is `<Navigate>` would fire its redirect during the
   * ~100-200ms cold-start window before the flag value is known.
   */
  pending?: ReactNode;
  /**
   * Invert the gate. When `true`, render `children` when the flag is OFF.
   * Useful for `<FlagGate flag="maintenance-mode" invert><NormalApp /></FlagGate>`
   * — show the normal app unless maintenance-mode is on.
   */
  invert?: boolean;
}

/**
 * Renders `children` based on an Unleash feature flag.
 *
 * Loading behavior: while the SDK is still bootstrapping its flags on first
 * paint (~100-200ms on cold start, ~0ms with cached values), this renders
 * `pending` (default `null`) and does NOT evaluate `fallback`. Only once the
 * flags have actually resolved (`flagsReady`) does it decide between `children`
 * and `fallback`. This guarantees a route-level gate never fires its
 * `<Navigate>` fallback during the cold-start window — which would otherwise
 * bounce first-time/anonymous visitors before the flag is even known.
 * See docs/feature-flags/README.md.
 *
 * If the FlagProvider isn't mounted (env vars missing → Tom's defensive guard
 * in `src/main.tsx` skips the provider), the build-time `unleashEnabled`
 * constant is `false`. In that case the SDK's default context reports
 * `flagsReady: false` permanently and never flips — so we must NOT block on it.
 * The loading short-circuit is gated on `unleashEnabled`, letting the gate fall
 * through to `shouldShow` (where `useFlag` returns `false` cleanly without a
 * provider) and render `fallback` — the original "safe outside the provider"
 * contract. Prod and dev always inject the token via cloudbuild, so the
 * provider is mounted, `unleashEnabled` is `true`, and `flagsReady` resolves
 * normally.
 *
 * @example
 *   // Hide nav entry when flag is off
 *   <FlagGate flag="community-board">
 *     <NavLink to="/community">Community</NavLink>
 *   </FlagGate>
 *
 *   // Route-level gate with redirect — show a loader during bootstrap so cold
 *   // traffic doesn't briefly redirect to "/" before the flag resolves
 *   <FlagGate flag="nextgen-access" fallback={<Navigate to="/" replace />} pending={<PageLoader />}>
 *     <SalesPage />
 *   </FlagGate>
 *
 *   // Kill switch — show normal app unless maintenance-mode is on
 *   <FlagGate flag="maintenance-mode" invert>
 *     <App />
 *   </FlagGate>
 */
/**
 * The SAME four-state decision FlagGate makes, for callers that need the answer
 * as a value rather than as rendered children — e.g. a grid whose COLUMN COUNT
 * depends on whether a flag-gated child will render.
 *
 * Exists so that decision has exactly one implementation. A caller that reaches
 * for `useFlag` directly re-derives the wrong THREE-state version and silently
 * misses the fourth state: when the env vars are absent at build time,
 * `src/main.tsx` omits <FlagProvider> entirely, `flagsReady` is pinned false
 * forever, and any `flagsReady || flagsError` guard is false for the life of the
 * page. That is the trap this component's docblock has always warned about;
 * before this hook existed there was no way to heed the warning and still branch
 * on a flag outside JSX.
 *
 * `resolved` is false only during the genuine SDK bootstrap window. Treat an
 * unresolved flag as OFF for layout purposes — that matches production for any
 * flag that is prod-OFF, so the common path does not reflow.
 */
export function useFlagDecision(flag: string, invert = false): { resolved: boolean; on: boolean } {
  const { flagsReady } = useFlagsStatus();
  const enabled = useFlag(flag);
  const resolved = !unleashEnabled || flagsReady;
  const on = invert ? !enabled : enabled;
  return { resolved, on: resolved ? on : false };
}

export function FlagGate({ flag, children, fallback = null, pending = null, invert = false }: FlagGateProps) {
  const { flagsReady } = useFlagsStatus();
  const enabled = useFlag(flag);

  // Don't evaluate the flag (or its fallback) until the SDK has resolved.
  // Route gates whose fallback is <Navigate> must not redirect during bootstrap.
  // Gate on `unleashEnabled`: when the provider was never mounted (no token),
  // `flagsReady` is perpetually false — so we skip the short-circuit and fall
  // through to `shouldShow`/`fallback` (the "safe outside the provider" path).
  if (unleashEnabled && !flagsReady) {
    return <>{pending ?? null}</>;
  }

  const shouldShow = invert ? !enabled : enabled;
  return <>{shouldShow ? children : fallback}</>;
}
